/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useRef, useState, type CSSProperties, type FormEvent, useEffect } from "react";
import { Navigate } from "react-router";
import { useAuth, type TwoFactorChallenge } from "../auth/AuthContext";
import { googleClientId, renderGoogleButton } from "../auth/googleSignIn";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Logo } from "../components/ui/Logo";
import { ManateeMark } from "../components/ui/ManateeMark";
import { Spinner } from "../components/ui/Spinner";
import { ValidationError } from "../lib/validation";
import type { FieldError } from "../api/client";

const TYPED_PHRASES = [
  "supervising the agents.",
  "the families in care.",
  "the care team.",
  "keeping care moving.",
];

const TYPE_MS = 55;
const DELETE_MS = 28;
const HOLD_MS = 2600;
const GAP_MS = 350;

/**
 * The cycling half of the headline: types a phrase, holds, deletes, types the next. Lives inside
 * a height-reserved line so the panel never shifts; reduced-motion users get the first phrase
 * static.
 */
function TypedCycle() {
  const [text, setText] = useState("");
  const [caretOn, setCaretOn] = useState(true);

  useEffect(() => {
    const prefersReduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setText(TYPED_PHRASES[0] ?? "");
      setCaretOn(false);
      return;
    }
    let phrase = 0;
    let position = 0;
    let deleting = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      const current = TYPED_PHRASES[phrase] ?? "";
      if (!deleting) {
        position += 1;
        setText(current.slice(0, position));
        if (position === current.length) {
          deleting = true;
          timer = setTimeout(tick, HOLD_MS);
          return;
        }
        timer = setTimeout(tick, TYPE_MS);
        return;
      }
      position -= 1;
      setText(current.slice(0, position));
      if (position === 0) {
        deleting = false;
        phrase = (phrase + 1) % TYPED_PHRASES.length;
        timer = setTimeout(tick, GAP_MS);
        return;
      }
      timer = setTimeout(tick, DELETE_MS);
    };

    timer = setTimeout(tick, 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <span className="login-typed block">
      {text}
      {caretOn && <span className="login-caret" aria-hidden />}
    </span>
  );
}

/**
 * The workspace door: Google renders its own button into the reserved slot, so the layout never
 * jumps. Sign-in failures land in the same error surface as the password form.
 */
function GoogleSignIn({ onError }: { onError: (message: string) => void }) {
  const { loginWithGoogle } = useAuth();
  const slot = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");

  useEffect(() => {
    const container = slot.current;
    if (container === null) {
      return;
    }
    let cancelled = false;
    void renderGoogleButton(container, (credential) => {
      void loginWithGoogle(credential).then((result) => {
        if (!result.ok && !cancelled) {
          onError(result.error.message);
        }
      });
    })
      .then(() => {
        if (!cancelled) {
          setState("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState("failed");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loginWithGoogle, onError]);

  return (
    <div>
      <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-text-tertiary">
        <span className="h-px flex-1 bg-border" />
        or
        <span className="h-px flex-1 bg-border" />
      </div>
      <div className="relative">
        <div ref={slot} className="flex min-h-11 justify-center" />
        {state === "loading" && (
          <div className="absolute inset-0 grid place-items-center" aria-hidden>
            <div className="h-10 w-full max-w-[400px] animate-pulse rounded-full bg-bg-hover" />
          </div>
        )}
      </div>
      {state === "failed" && (
        <p className="mt-2 text-center text-sm text-text-tertiary">
          Google sign-in could not be loaded. Check your connection and reload.
        </p>
      )}
    </div>
  );
}

const CODE_PATTERN = /^\d{6}$/;

function enter(delayMs: number): CSSProperties {
  return { "--enter-delay": `${delayMs}ms` } as CSSProperties;
}

function validateCredentials(email: string, password: string): ValidationError | null {
  const errors: FieldError[] = [];
  if (email.trim() === "") {
    errors.push({ field: "email", message: "Email is required" });
  } else if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
    errors.push({ field: "email", message: "This doesn't look like an email address" });
  }
  if (password === "") {
    errors.push({ field: "password", message: "Password is required" });
  }
  return errors.length > 0 ? new ValidationError("Please fix the errors below.", errors) : null;
}

function validateCode(code: string): ValidationError | null {
  if (CODE_PATTERN.test(code.trim())) {
    return null;
  }
  return new ValidationError("Please fix the errors below.", [
    { field: "code", message: "Enter the 6-digit code we texted you" },
  ]);
}

export function Login() {
  const { session, login, verifyTwoFactor, resendTwoFactorCode } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [code, setCode] = useState("");
  const [notice, setNotice] = useState("");
  const [validation, setValidation] = useState<ValidationError | null>(null);
  const [busy, setBusy] = useState(false);

  if (session.state === "authenticated") {
    return <Navigate to="/" replace />;
  }

  function clearFieldError(field: string) {
    setValidation((current) => current?.withoutField(field) ?? null);
  }

  async function submitCredentials(event: FormEvent) {
    event.preventDefault();
    setValidation(null);
    const invalid = validateCredentials(email, password);
    if (invalid !== null) {
      setValidation(invalid);
      return;
    }
    setBusy(true);
    const result = await login(email.trim(), password);
    setBusy(false);
    if (!result.ok) {
      setValidation(ValidationError.fromApiError(result.error));
      return;
    }
    if (result.value !== null) {
      setChallenge(result.value);
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    if (challenge === null) {
      return;
    }
    setValidation(null);
    setNotice("");
    const invalid = validateCode(code);
    if (invalid !== null) {
      setValidation(invalid);
      return;
    }
    setBusy(true);
    const result = await verifyTwoFactor(challenge.token, code.trim());
    setBusy(false);
    if (!result.ok) {
      setValidation(ValidationError.fromApiError(result.error));
    }
  }

  async function resendCode() {
    if (challenge === null) {
      return;
    }
    setValidation(null);
    setNotice("");
    setBusy(true);
    const result = await resendTwoFactorCode(challenge.token);
    setBusy(false);
    if (result.ok) {
      setNotice("We sent a new code.");
    } else {
      setValidation(ValidationError.fromApiError(result.error));
    }
  }

  function backToSignIn() {
    setChallenge(null);
    setCode("");
    setPassword("");
    setNotice("");
    setValidation(null);
  }

  const generalError =
    validation !== null && validation.fieldErrors.length === 0 ? validation.errorMessage : null;

  return (
    <div className="grid min-h-dvh bg-bg-base lg:grid-cols-2">
      {/* Brand panel, lg and up */}
      <div className="login-panel relative hidden overflow-hidden lg:flex lg:flex-col lg:items-center lg:justify-center lg:p-12">
        <div className="login-glow left-1/4 top-1/4" />
        <div className="relative flex flex-col items-center text-center">
          <ManateeMark animate className="h-36 text-white" />
          <h2
            className="enter mt-9 max-w-md font-display text-4xl leading-tight text-white"
            style={enter(900)}
          >
            The console for
            <TypedCycle />
          </h2>
        </div>
        <div
          className="enter absolute inset-x-0 bottom-10 text-center text-sm tracking-[0.08em] text-white/45"
          style={enter(1200)}
        >
          Manatee · Internal · Please mind the PHI
        </div>
      </div>

      {/* Sign-in */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="enter mb-10 flex flex-col items-center gap-4 lg:items-start">
            <ManateeMark animate className="h-14 text-text-primary lg:hidden" />
            <Logo variant="type" className="h-6 text-text-primary lg:h-7" />
            <p className="text-sm text-text-secondary">
              {challenge === null
                ? "Sign in to the staff console"
                : challenge.message !== ""
                  ? challenge.message
                  : `Enter the code we sent to ${challenge.phone}`}
            </p>
          </div>
          {challenge === null ? (
            <form onSubmit={submitCredentials} noValidate className="space-y-4">
              <div className="enter" style={enter(120)}>
                <Input
                  label="Email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  error={validation?.getFieldError("email") ?? ""}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearFieldError("email");
                  }}
                />
              </div>
              <div className="enter" style={enter(220)}>
                <Input
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  error={validation?.getFieldError("password") ?? ""}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearFieldError("password");
                  }}
                />
              </div>
              {generalError !== null && (
                <p role="alert" className="text-sm font-medium text-error">
                  {generalError}
                </p>
              )}
              <div className="enter" style={enter(320)}>
                <Button type="submit" disabled={busy} className="w-full" size="md">
                  {busy ? (
                    <>
                      <Spinner size="xs" className="mr-2 text-bg-base" />
                      Signing in…
                    </>
                  ) : (
                    "Sign in"
                  )}
                </Button>
              </div>
              {googleClientId !== "" && (
                <div className="enter" style={enter(420)}>
                  <GoogleSignIn
                    onError={(message) =>
                      setValidation(new ValidationError(message, []))
                    }
                  />
                </div>
              )}
            </form>
          ) : (
            <form onSubmit={submitCode} noValidate className="space-y-4">
              <div className="enter" style={enter(120)}>
                <Input
                  label="Verification code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  error={validation?.getFieldError("code") ?? ""}
                  onChange={(e) => {
                    setCode(e.target.value);
                    clearFieldError("code");
                  }}
                />
              </div>
              {generalError !== null && (
                <p role="alert" className="text-sm font-medium text-error">
                  {generalError}
                </p>
              )}
              {notice !== "" && (
                <p role="status" className="text-sm text-text-secondary">
                  {notice}
                </p>
              )}
              <div className="enter" style={enter(220)}>
                <Button type="submit" disabled={busy} className="w-full" size="md">
                  {busy ? (
                    <>
                      <Spinner size="xs" className="mr-2 text-bg-base" />
                      Verifying…
                    </>
                  ) : (
                    "Verify"
                  )}
                </Button>
              </div>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  disabled={busy}
                  onClick={resendCode}
                  className="text-interactive hover:underline disabled:opacity-50"
                >
                  Resend code
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={backToSignIn}
                  className="text-text-secondary hover:underline disabled:opacity-50"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
